import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, User, AlertCircle, ShieldCheck, KeyRound } from 'lucide-react';

const AdminLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA step-up state
  const [twoFA, setTwoFA] = useState(null); // { pending_token, email, methods_available }
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const { login, register, verify2FA, request2FAEmailCode } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password, rememberMe);
        if (result?.requires_2fa) {
          setTwoFA(result);
          setLoading(false);
          return;
        }
        const adminRoles = ['admin', 'super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'];
        if (adminRoles.includes(result.role)) {
          navigate('/admin/dashboard');
        } else {
          setError('Admin access required');
        }
      } else {
        await register(name, email, password);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setTwoFAError('');
    setTwoFALoading(true);
    try {
      const user = await verify2FA(twoFA.pending_token, twoFACode.trim(), rememberMe);
      const adminRoles = ['admin', 'super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'];
      if (adminRoles.includes(user.role)) {
        navigate('/admin/dashboard');
      } else {
        setTwoFAError('Admin access required');
      }
    } catch (err) {
      setTwoFAError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleSendEmailCode = async () => {
    setTwoFAError('');
    try {
      await request2FAEmailCode(twoFA.pending_token);
      setEmailSent(true);
    } catch (err) {
      setTwoFAError(err.response?.data?.detail || 'Could not send code');
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E5E7EB] p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/5fygylhw_IMG_0281.jpeg"
                alt="Novena Foundation"
                className="w-16 h-16 mx-auto"
              />
            </Link>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">
              Admin {isLogin ? 'Login' : 'Register'}
            </h1>
            <p className="text-sm text-[#4B5563] mt-2">
              {isLogin ? 'Sign in to access the admin dashboard' : 'Create an admin account'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* 2FA STEP-UP */}
          {twoFA ? (
            <div data-testid="two-factor-step">
              <div className="text-center mb-5">
                <ShieldCheck className="w-10 h-10 mx-auto text-[#BA1826] mb-2" />
                <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Two-Factor Authentication</h2>
                <p className="text-sm text-[#4B5563] mt-1">
                  Enter the 6-digit code from your authenticator app for <strong>{twoFA.email}</strong>.
                </p>
              </div>
              {twoFAError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{twoFAError}</span>
                </div>
              )}
              {emailSent && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-4 text-sm" data-testid="two-factor-email-sent">
                  Recovery code emailed to {twoFA.email}. It expires in 10 minutes.
                </div>
              )}
              <form onSubmit={handle2FASubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#4B5563] mb-1">
                    {useBackupCode ? 'Backup code (e.g., AXH6H-WDHUY)' : 'Authenticator code or email code'}
                  </label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <input
                      type="text"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10 font-mono tracking-widest text-center text-lg"
                      placeholder={useBackupCode ? 'XXXXX-XXXXX' : '123 456'}
                      autoFocus
                      data-testid="two-factor-code-input"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={twoFALoading || !twoFACode.trim()}
                  className="w-full btn-primary py-3 disabled:opacity-50"
                  data-testid="two-factor-verify-btn"
                >
                  {twoFALoading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>
              <div className="mt-5 flex flex-col gap-2 text-sm text-center">
                <button
                  type="button"
                  onClick={() => setUseBackupCode(v => !v)}
                  className="text-[#BA1826] hover:underline"
                  data-testid="two-factor-toggle-backup"
                >
                  {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
                </button>
                {twoFA.methods_available?.email_fallback && !emailSent && (
                  <button
                    type="button"
                    onClick={handleSendEmailCode}
                    className="text-[#4B5563] hover:underline"
                    data-testid="two-factor-email-fallback-btn"
                  >
                    Lost your device? Email me a recovery code
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setTwoFA(null); setTwoFACode(''); setTwoFAError(''); setEmailSent(false); }}
                  className="text-[#9CA3AF] hover:underline mt-2"
                  data-testid="two-factor-cancel"
                >
                  Cancel and sign in as a different user
                </button>
              </div>
            </div>
          ) : (
          <>
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-1">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                    placeholder="Your name"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                  placeholder="admin@novena.org"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#4B5563] mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between flex-wrap gap-2" data-testid="login-options-row">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-[#E5E7EB] text-[#BA1826] focus:ring-[#BA1826]"
                    data-testid="remember-me-checkbox"
                  />
                  <span className="text-sm text-[#4B5563]">Remember me for 30 days</span>
                </label>
                <Link
                  to="/admin/forgot-password"
                  className="text-sm text-[#BA1826] font-medium hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-[#E5E7EB]"></div>
            <span className="px-4 text-sm text-[#9CA3AF]">or</span>
            <div className="flex-1 border-t border-[#E5E7EB]"></div>
          </div>

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 border border-[#E5E7EB] rounded-sm hover:bg-[#F8F9FA] transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Toggle */}
          <p className="text-center text-sm text-[#4B5563] mt-6">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="ml-1 text-[#BA1826] font-medium hover:underline"
            >
              {isLogin ? 'Register' : 'Sign in'}
            </button>
          </p>

          {/* Back to site */}
          <div className="text-center mt-4">
            <Link to="/" className="text-sm text-[#4B5563] hover:text-[#BA1826]">
              ← Back to website
            </Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
